/**
 * Pro / Enterprise feature pages — Complaints, Vulnerable Customer, Annual
 * Reviews, Supervision sign-off. All gated server-side via _firm_plan; on
 * 402 the global TrialLimitModal will pop the upgrade prompt.
 */
import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Plus, Loader2, AlertTriangle, CheckCircle2, X, Clock, Users, Heart, CalendarCheck, ShieldQuestion, Inbox, FileCheck } from "lucide-react";
import api, { formatApiError } from "../lib/api";
import { track, EVT } from "../lib/analytics";

// ============================== Complaints ==============================
export function Complaints() {
  const [data, setData] = useState(null);
  const [showAdd, setShowAdd] = useState(false);
  const [form, setForm] = useState({ client_name: "", date_received: new Date().toISOString().slice(0, 10), nature: "", channel: "email", assigned_to: "" });
  const [busy, setBusy] = useState(false);

  const load = async () => {
    try { const { data } = await api.get("/api/complaints"); setData(data); }
    catch (e) { /* upgrade modal will surface 402 */ }
  };
  useEffect(() => { load(); }, []);

  const create = async () => {
    if (form.nature.length < 10) { alert("Please describe the complaint in at least 10 characters"); return; }
    setBusy(true);
    try { await api.post("/api/complaints", form); setShowAdd(false); setForm({ client_name: "", date_received: new Date().toISOString().slice(0, 10), nature: "", channel: "email", assigned_to: "" }); await load(); }
    catch (e) { alert(formatApiError(e)); }
    finally { setBusy(false); }
  };

  const advance = async (id, stage) => {
    const note = stage === "resolved" ? prompt("Resolution note") : (stage === "escalated" ? prompt("Reason for escalation") : "");
    try { await api.patch(`/api/complaints/${id}`, { stage, note }); await load(); }
    catch (e) { alert(formatApiError(e)); }
  };

  if (!data) return <div className="card2 p-8 text-center"><Loader2 className="animate-spin mx-auto text-slate2" /></div>;

  return (
    <div className="space-y-6" data-testid="complaints-page">
      <div className="flex justify-between items-center flex-wrap gap-2">
        <div>
          <h1 className="t-h1">Complaints log</h1>
          <p className="t-body-sm text-slate2 mt-1">FCA-aligned complaints tracking · 8-week resolution countdown</p>
        </div>
        <button onClick={() => setShowAdd(true)} className="btn btn-primary" data-testid="complaints-add"><Plus size={16}/>Log a complaint</button>
      </div>

      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
        {[{label:"Open", v:data.stats.open}, {label:"Resolved", v:data.stats.resolved}, {label:"Escalated", v:data.stats.escalated}, {label:"Total", v:data.stats.total}].map(s => (
          <div key={s.label} className="card2 p-4"><div className="t-label text-slate2">{s.label}</div><div className="t-h2 mt-1">{s.v}</div></div>
        ))}
      </div>

      <div className="card2 overflow-hidden hidden md:block">
        {data.items.length === 0 ? (
          <div className="p-10 text-center text-slate2"><Inbox className="mx-auto mb-2" /> No complaints logged yet.</div>
        ) : (
          <table className="w-full t-body-sm">
            <thead className="bg-bg2 text-left"><tr><th className="px-4 py-3">Client</th><th>Received</th><th>Channel</th><th>Stage</th><th>FCA deadline</th><th></th></tr></thead>
            <tbody>
              {data.items.map(c => (
                <tr key={c.id} className="border-t border-border2">
                  <td className="px-4 py-3">{c.client_name}</td>
                  <td>{c.date_received}</td>
                  <td className="capitalize">{c.channel}</td>
                  <td><span className={`badge2 ${c.stage === "resolved" ? "badge-verified" : c.stage === "escalated" ? "badge-warning" : "badge-pending"}`}>{c.stage.replace(/_/g," ")}</span></td>
                  <td className={c.days_to_deadline < 7 ? "text-rose-600 font-medium" : ""}>{c.days_to_deadline ?? "—"} days</td>
                  <td className="text-right pr-4">
                    {c.stage !== "resolved" && (
                      <select onChange={(e) => e.target.value && advance(c.id, e.target.value)} value="" className="text-xs border border-border2 rounded px-2 py-1">
                        <option value="">Update…</option>
                        <option value="acknowledged">Acknowledged</option>
                        <option value="under_investigation">Under investigation</option>
                        <option value="resolved">Resolved</option>
                        <option value="escalated">Escalate</option>
                      </select>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      {/* Mobile cards */}
      <div className="md:hidden space-y-3">
        {data.items.length === 0 ? (
          <div className="card2 p-10 text-center text-slate2"><Inbox className="mx-auto mb-2" /> No complaints logged yet.</div>
        ) : data.items.map(c => (
          <div key={c.id} className="card2 p-4" data-testid={`complaint-card-${c.id}`}>
            <div className="flex items-start justify-between gap-2 mb-2">
              <div className="font-semibold">{c.client_name}</div>
              <span className={`badge2 ${c.stage === "resolved" ? "badge-verified" : c.stage === "escalated" ? "badge-warning" : "badge-pending"}`}>{c.stage.replace(/_/g," ")}</span>
            </div>
            <div className="grid grid-cols-2 gap-2 t-caption mb-3">
              <div><span className="text-slate2">Received:</span> {c.date_received}</div>
              <div className="capitalize"><span className="text-slate2">Channel:</span> {c.channel}</div>
              <div className={`col-span-2 ${c.days_to_deadline < 7 ? "text-rose-600 font-medium" : ""}`}>FCA deadline: {c.days_to_deadline ?? "—"} days</div>
            </div>
            {c.stage !== "resolved" && (
              <select onChange={(e) => e.target.value && advance(c.id, e.target.value)} value="" className="w-full input2 text-sm">
                <option value="">Update stage…</option>
                <option value="acknowledged">Acknowledged</option>
                <option value="under_investigation">Under investigation</option>
                <option value="resolved">Resolved</option>
                <option value="escalated">Escalate</option>
              </select>
            )}
          </div>
        ))}
      </div>

      {showAdd && (
        <div className="fixed inset-0 z-50 bg-black/60 flex items-center justify-center p-4" onClick={() => setShowAdd(false)}>
          <div className="card2 max-w-md w-full p-6" onClick={(e) => e.stopPropagation()}>
            <div className="flex justify-between items-center mb-4"><h3 className="t-h3">Log a complaint</h3><button onClick={() => setShowAdd(false)}><X size={18}/></button></div>
            <div className="space-y-3">
              <input className="input2 w-full" placeholder="Client name" value={form.client_name} onChange={(e)=>setForm({...form, client_name:e.target.value})} data-testid="complaint-client" />
              <input type="date" className="input2 w-full" value={form.date_received} onChange={(e)=>setForm({...form, date_received:e.target.value})} />
              <select className="input2 w-full" value={form.channel} onChange={(e)=>setForm({...form, channel:e.target.value})}>
                <option value="email">Email</option><option value="phone">Phone</option><option value="letter">Letter</option><option value="portal">Portal</option><option value="other">Other</option>
              </select>
              <textarea className="input2 w-full min-h-[100px]" placeholder="Nature of complaint (10+ chars)" value={form.nature} onChange={(e)=>setForm({...form, nature:e.target.value})} data-testid="complaint-nature" />
              <button onClick={create} disabled={busy} className="btn btn-primary w-full" data-testid="complaint-create">{busy ? <Loader2 size={14} className="animate-spin"/> : "Log complaint"}</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}


// ============================== Vulnerable ==============================
export function VulnerableCustomers() {
  const [items, setItems] = useState(null);
  const [showAdd, setShowAdd] = useState(false);
  const [form, setForm] = useState({ client_id: "", vulnerability_type: "financial_difficulty", care_documented: "" });
  const [busy, setBusy] = useState(false);

  const load = async () => { try { const { data } = await api.get("/api/vulnerable-customers"); setItems(data.items || []); } catch {} };
  useEffect(() => { load(); }, []);

  const create = async () => {
    if (form.care_documented.length < 50) { alert("Care documentation must be at least 50 characters"); return; }
    setBusy(true);
    try { await api.post("/api/vulnerable-customers", form); setShowAdd(false); await load(); }
    catch (e) { alert(formatApiError(e)); }
    finally { setBusy(false); }
  };

  if (!items) return <div className="card2 p-8 text-center"><Loader2 className="animate-spin mx-auto text-slate2" /></div>;

  return (
    <div className="space-y-6" data-testid="vulnerable-page">
      <div className="flex justify-between items-center flex-wrap gap-2">
        <div>
          <h1 className="t-h1">Vulnerable customers</h1>
          <p className="t-body-sm text-slate2 mt-1">Consumer Duty · documented additional care</p>
        </div>
        <button onClick={() => setShowAdd(true)} className="btn btn-primary" data-testid="vulnerable-add"><Plus size={16}/>Flag a client</button>
      </div>

      <div className="card2 overflow-hidden hidden md:block">
        {items.length === 0 ? (
          <div className="p-10 text-center text-slate2"><Heart className="mx-auto mb-2" /> No vulnerable customer flags on record.</div>
        ) : (
          <table className="w-full t-body-sm">
            <thead className="bg-bg2 text-left"><tr><th className="px-4 py-3">Client</th><th>Type</th><th>Flagged</th><th>Care notes</th></tr></thead>
            <tbody>
              {items.map(f => (
                <tr key={f.id} className="border-t border-border2">
                  <td className="px-4 py-3"><Link to={`/app/clients/${f.client_id}`} className="text-indigo2 hover:underline">{f.client_id?.slice(0,12)}</Link></td>
                  <td className="capitalize">{(f.vulnerability_type || "").replace(/_/g," ")}</td>
                  <td>{f.created_at?.slice(0,10)}</td>
                  <td className="max-w-md truncate">{f.care_documented}</td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
      <div className="md:hidden space-y-3">
        {items.length === 0 ? (
          <div className="card2 p-10 text-center text-slate2"><Heart className="mx-auto mb-2" /> No vulnerable customer flags on record.</div>
        ) : items.map(f => (
          <div key={f.id} className="card2 p-4" data-testid={`vulnerable-card-${f.id}`}>
            <div className="flex justify-between items-start gap-2 mb-2">
              <Link to={`/app/clients/${f.client_id}`} className="text-indigo2 font-mono text-xs">{f.client_id?.slice(0,12)}</Link>
              <span className="badge2 badge-warning capitalize">{(f.vulnerability_type||"").replace(/_/g," ")}</span>
            </div>
            <div className="t-caption mb-2">Flagged {f.created_at?.slice(0,10)}</div>
            <div className="t-body-sm text-slate2 line-clamp-3">{f.care_documented}</div>
          </div>
        ))}
      </div>

      {showAdd && (
        <div className="fixed inset-0 z-50 bg-black/60 flex items-center justify-center p-4" onClick={() => setShowAdd(false)}>
          <div className="card2 max-w-md w-full p-6" onClick={(e) => e.stopPropagation()}>
            <div className="flex justify-between items-center mb-4"><h3 className="t-h3">Flag vulnerable customer</h3><button onClick={() => setShowAdd(false)}><X size={18}/></button></div>
            <div className="space-y-3">
              <input className="input2 w-full" placeholder="Client ID" value={form.client_id} onChange={(e)=>setForm({...form, client_id:e.target.value})} />
              <select className="input2 w-full" value={form.vulnerability_type} onChange={(e)=>setForm({...form, vulnerability_type:e.target.value})}>
                <option value="financial_difficulty">Financial difficulty</option>
                <option value="health">Health</option>
                <option value="life_events">Life events</option>
                <option value="low_resilience">Low financial resilience</option>
                <option value="low_capability">Low capability</option>
              </select>
              <textarea className="input2 w-full min-h-[120px]" placeholder="Documented additional care taken (50+ characters)" value={form.care_documented} onChange={(e)=>setForm({...form, care_documented:e.target.value})} />
              <div className="t-caption">{form.care_documented.length} / 50 chars minimum</div>
              <button onClick={create} disabled={busy || form.care_documented.length < 50} className="btn btn-primary w-full">{busy ? <Loader2 size={14} className="animate-spin"/> : "Flag client"}</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}


// ============================== Annual Reviews ==============================
export function AnnualReviews() {
  const [data, setData] = useState(null);
  const load = async () => { try { const { data } = await api.get("/api/annual-reviews"); setData(data); } catch {} };
  useEffect(() => { load(); }, []);
  const complete = async (clientId) => {
    const outcome = prompt("Review outcome (10+ characters):");
    if (!outcome || outcome.length < 10) return;
    try { await api.post("/api/annual-reviews/complete", { client_id: clientId, outcome }); await load(); }
    catch (e) { alert(formatApiError(e)); }
  };
  if (!data) return <div className="card2 p-8 text-center"><Loader2 className="animate-spin mx-auto text-slate2" /></div>;

  return (
    <div className="space-y-6" data-testid="annual-reviews-page">
      <h1 className="t-h1">Annual reviews</h1>
      <div className="grid grid-cols-3 gap-3">
        <div className="card2 p-4"><div className="t-label text-slate2">Upcoming</div><div className="t-h2 mt-1">{data.counts.upcoming}</div></div>
        <div className="card2 p-4"><div className="t-label text-slate2 text-rose-600">Overdue</div><div className="t-h2 mt-1 text-rose-600">{data.counts.overdue}</div></div>
        <div className="card2 p-4"><div className="t-label text-slate2">Completed</div><div className="t-h2 mt-1">{data.counts.completed}</div></div>
      </div>

      {["overdue", "upcoming", "completed"].map((bucket) => (
        <section key={bucket}>
          <h2 className="t-h3 mb-2 capitalize">{bucket}</h2>
          <div className="card2 overflow-hidden">
            {data[bucket].length === 0 ? <div className="p-8 text-center text-slate2 t-body-sm">None.</div> : (
              <table className="w-full t-body-sm">
                <thead className="bg-bg2 text-left"><tr><th className="px-4 py-3">Client</th><th>Review date</th><th>Days</th><th></th></tr></thead>
                <tbody>
                  {data[bucket].map(c => (
                    <tr key={c.id} className="border-t border-border2">
                      <td className="px-4 py-3">{c.first_name} {c.last_name}</td>
                      <td>{c.annual_review_date}</td>
                      <td className={c.days_until_review < 0 ? "text-rose-600" : ""}>{c.days_until_review}</td>
                      <td className="text-right pr-4">
                        {bucket !== "completed" && <button onClick={() => complete(c.id)} className="btn btn-secondary btn-sm">Mark complete</button>}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>
        </section>
      ))}
    </div>
  );
}


// ============================== Supervision ==============================
export function Supervision() {
  const [view, setView] = useState("awaiting_me");
  const [items, setItems] = useState(null);
  const load = async () => { try { const { data } = await api.get(`/api/supervision?view=${view}`); setItems(data.items || []); } catch {} };
  useEffect(() => { load(); }, [view]);
  const decide = async (id, decision) => {
    const reason = prompt(`Reason for ${decision} (10+ chars):`);
    if (!reason || reason.length < 10) return;
    try { await api.post(`/api/supervision/${id}/decide`, { decision, reason }); await load(); }
    catch (e) { alert(formatApiError(e)); }
  };
  if (!items) return <div className="card2 p-8 text-center"><Loader2 className="animate-spin mx-auto text-slate2" /></div>;

  return (
    <div className="space-y-6" data-testid="supervision-page">
      <h1 className="t-h1">Supervision & sign-off</h1>
      <div className="inline-flex rounded-full border border-border2 p-1 bg-bg2 text-sm">
        <button onClick={() => setView("awaiting_me")} className={`px-3 py-1 rounded-full ${view==="awaiting_me"?"bg-white shadow-sm font-medium":""}`}>Awaiting my approval</button>
        <button onClick={() => setView("submitted")} className={`px-3 py-1 rounded-full ${view==="submitted"?"bg-white shadow-sm font-medium":""}`}>Submitted by me</button>
        <button onClick={() => setView("all")} className={`px-3 py-1 rounded-full ${view==="all"?"bg-white shadow-sm font-medium":""}`}>All</button>
      </div>

      <div className="card2 overflow-hidden">
        {items.length === 0 ? <div className="p-10 text-center text-slate2"><FileCheck className="mx-auto mb-2"/>Nothing in this view yet.</div> : (
          <table className="w-full t-body-sm">
            <thead className="bg-bg2 text-left"><tr><th className="px-4 py-3">Type</th><th>Resource</th><th>Submitter → Approver</th><th>Status</th><th></th></tr></thead>
            <tbody>
              {items.map(a => (
                <tr key={a.id} className="border-t border-border2">
                  <td className="px-4 py-3 capitalize">{a.resource_type.replace(/_/g," ")}</td>
                  <td className="t-mono text-xs">{a.resource_id?.slice(0,12)}</td>
                  <td>{a.submitter_email} → {a.approver_email}</td>
                  <td><span className={`badge2 ${a.status === "approved" ? "badge-verified" : a.status === "rejected" ? "badge-warning" : "badge-pending"}`}>{a.status}</span></td>
                  <td className="text-right pr-4 space-x-1">
                    {view === "awaiting_me" && a.status === "pending" && <>
                      <button onClick={() => decide(a.id, "approved")} className="btn btn-secondary btn-sm">Approve</button>
                      <button onClick={() => decide(a.id, "rejected")} className="btn btn-ghost btn-sm text-rose-600">Reject</button>
                    </>}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
}


// ============================== AML Screenings list ==============================
export function AmlScreenings() {
  const [items, setItems] = useState(null);
  const load = async () => { try { const { data } = await api.get("/api/aml/screenings"); setItems(data.items || []); } catch {} };
  useEffect(() => { load(); }, []);
  const review = async (id) => {
    const reason = prompt("Review reason (20+ characters):");
    if (!reason || reason.length < 20) { alert("Review reason must be at least 20 characters"); return; }
    try { await api.post(`/api/aml/screenings/${id}/review`, { reason }); track(EVT.aml_flag_reviewed, { screening_id: id }); await load(); }
    catch (e) { alert(formatApiError(e)); }
  };
  if (!items) return <div className="card2 p-8 text-center"><Loader2 className="animate-spin mx-auto text-slate2" /></div>;

  return (
    <div className="space-y-6" data-testid="aml-page">
      <h1 className="t-h1">AML screenings</h1>
      <div className="card2 overflow-hidden hidden md:block">
        {items.length === 0 ? <div className="p-10 text-center text-slate2"><ShieldQuestion className="mx-auto mb-2"/>No screenings on record yet.</div> : (
          <table className="w-full t-body-sm">
            <thead className="bg-bg2 text-left"><tr><th className="px-4 py-3">Name</th><th>Status</th><th>Hits</th><th>Date</th><th>Trigger</th><th></th></tr></thead>
            <tbody>
              {items.map(s => (
                <tr key={s.id} className="border-t border-border2">
                  <td className="px-4 py-3">{s.name}</td>
                  <td><span className={`badge2 ${s.status === "clear" ? "badge-verified" : s.status === "possible_match" ? "badge-warning" : "badge-pending"}`}>{s.status}</span></td>
                  <td className="t-mono">{s.total_hits}</td>
                  <td>{s.created_at?.slice(0,10)}</td>
                  <td className="capitalize">{(s.trigger||"").replace(/_/g," ")}</td>
                  <td className="text-right pr-4">
                    {s.status === "possible_match" && !s.reviewed && <button onClick={() => review(s.id)} className="btn btn-secondary btn-sm">Review</button>}
                    {s.reviewed && <CheckCircle2 size={14} className="text-emerald2 inline-block"/>}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
      <div className="md:hidden space-y-3">
        {items.length === 0 ? <div className="card2 p-10 text-center text-slate2"><ShieldQuestion className="mx-auto mb-2"/>No screenings on record yet.</div> : items.map(s => (
          <div key={s.id} className="card2 p-4" data-testid={`aml-card-${s.id}`}>
            <div className="flex justify-between items-start gap-2 mb-2">
              <div className="font-semibold">{s.name}</div>
              <span className={`badge2 ${s.status === "clear" ? "badge-verified" : s.status === "possible_match" ? "badge-warning" : "badge-pending"}`}>{s.status}</span>
            </div>
            <div className="grid grid-cols-2 t-caption mb-2">
              <div>Hits: <span className="t-mono">{s.total_hits}</span></div>
              <div>{s.created_at?.slice(0,10)}</div>
              <div className="capitalize col-span-2">Trigger: {(s.trigger||"").replace(/_/g," ")}</div>
            </div>
            {s.status === "possible_match" && !s.reviewed && <button onClick={() => review(s.id)} className="btn btn-secondary btn-sm w-full">Review</button>}
            {s.reviewed && <div className="t-caption text-emerald2 flex items-center gap-1"><CheckCircle2 size={12}/>Reviewed</div>}
          </div>
        ))}
      </div>
    </div>
  );
}
